import {
  createContext, useReducer, Reducer, useRef, FC, useContext,
} from 'react';
import * as mime from 'mime-types';
import { notification } from 'antd';

import { blobToBase64 } from '@utils/utils';
import PreviewModal from '../modalHouse/modals/shared/PreviewModal';

type InitialState = {
  showModal: boolean;
  data?: Blob;
  filename?: string;
  mimeType?: string;
  dataUrl?: string;
};

enum ActionType {
  OpenModal = 'openModal',
  CloseModal = 'closeModal',
  PreviewMedia = 'previewMedia',
}

type Action =
  | { type: ActionType.CloseModal }
  | { type: ActionType.OpenModal }
  | {
    type: ActionType.PreviewMedia;
    payload: {
      data: Blob;
      filename: string;
      mimeType: string;
      dataUrl: string;
    }
  };

const reducer: Reducer<InitialState, Action> = (
  state,
  action,
) => {
  switch (action.type) {
    case ActionType.OpenModal:
      return {
        ...state,
        showModal: true,
      };
    case ActionType.CloseModal:
      return {
        ...state,
        showModal: false,
      };
    case ActionType.PreviewMedia:
      return {
        ...state,
        data: action.payload.data,
        filename: action.payload.filename,
        mimeType: action.payload.mimeType,
        dataUrl: action.payload.dataUrl,
        showModal: true,
      };
    default:
      return state;
  }
};

type PreviewMediaContextType = {
  previewMedia: (filename: string, data: Blob) => void;
  closePreviewModal: () => void;
  state: InitialState;
};

export const PreviewMediaContext = createContext<PreviewMediaContextType | null>(null);

export const usePreviewMediaContext = () => useContext(PreviewMediaContext);

export const PreviewMediaProvider: FC = ({ children }) => {
  const { current: initialState } = useRef<InitialState>({ showModal: false });
  const [state, dispatch] = useReducer(reducer, initialState);

  const previewMedia = (filename: string, data: Blob) => {
    const mimeType = data.type || mime.lookup(filename);
    if (mimeType && mimeType.includes('pdf')) {
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl);
    } else if (mimeType) {
      blobToBase64(data, (newDataUrl) => {
        dispatch({
          type: ActionType.PreviewMedia,
          payload: {
            data,
            dataUrl: newDataUrl,
            filename,
            mimeType,
          },
        });
      });
    } else {
      notification.error({
        message: 'View Media',
        description: 'Invalid mimeType. Please contact technical support.',
      });
    }
  };

  const closePreviewModal = () => dispatch({ type: ActionType.CloseModal });

  return (
    <PreviewMediaContext.Provider value={{ state, closePreviewModal, previewMedia }}>
      <PreviewModal
        closeModal={closePreviewModal}
        data={state?.data}
        dataUrl={state?.dataUrl}
        filename={state.filename}
        mimeType={state.mimeType}
        showModal={state.showModal}
      />
      {children}
    </PreviewMediaContext.Provider>
  );
};
